import { ChangeEvent, useMemo } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { AXNUIContent, AXNUIContentConfirmButton, AXNUITheme, AXNUIThemeConfirmButton } from "@iddataweb/idw-common-ui/types";
import { SelectOption } from "@idw-types/common";
import { ThemingActionButtonValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { RadioInputFormGroup } from "../../../../../common/radio-input-form-group/radio-input-form-group";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingActionButtonDefaultSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    axnuiContent: AXNUIContent;
    axnuiTheme: AXNUITheme;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    validationErrors: ThemingActionButtonValidationErrors;
    updateAXNUIContentConfirmButtonFieldValue: (key: keyof AXNUIContentConfirmButton, value: string) => void;
    updateAXNUIThemeConfirmButtonFieldValue: (key: keyof AXNUIThemeConfirmButton, value: string | boolean) => void;
};

export const ThemingActionButtonDefaultSection = (props: ThemingActionButtonDefaultSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        axnuiContent,
        axnuiTheme,
        inheritedAXNUIContent,
        inheritedAXNUITheme,
        pageLevel,
        validationErrors,
        updateAXNUIContentConfirmButtonFieldValue,
        updateAXNUIThemeConfirmButtonFieldValue
    } = props;

    const handleUseCustomButtonTextChange = (newValue: string) => {
        updateAXNUIThemeConfirmButtonFieldValue("useCustomText", isStringTrue(newValue));
    };

    const handleResetUseCustomButtonText = () => {
        updateAXNUIThemeConfirmButtonFieldValue("useCustomText", inheritedAXNUITheme.confirmButton.useCustomText);
    };

    const handleDefaultButtonTextChange = async (newValue: SelectOption) => {
        updateAXNUIThemeConfirmButtonFieldValue("type", newValue.value);
    };

    const handleResetDefaultButtonText = () => {
        updateAXNUIThemeConfirmButtonFieldValue("type", inheritedAXNUITheme.confirmButton.type);
    };

    const handleCustomButtonTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentConfirmButtonFieldValue("buttonText", event.target.value);
    };

    const handleResetCustomButtonText = () => {
        updateAXNUIContentConfirmButtonFieldValue("buttonText", inheritedAXNUIContent.confirmButton.buttonText);
    };

    const defaultButtonTextOptions = useMemo(() => {
        return [
            { label: inheritedAXNUIContent.submitButtonType.continue, value: "continue" },
            { label: inheritedAXNUIContent.submitButtonType.confirm, value: "confirm" },
            { label: inheritedAXNUIContent.submitButtonType.back, value: "back" }
        ];
    }, [
        inheritedAXNUIContent.submitButtonType.continue,
        inheritedAXNUIContent.submitButtonType.confirm,
        inheritedAXNUIContent.submitButtonType.back
    ]);

    const selectedDefaultButtonTextOption = useMemo(() => {
        if (axnuiTheme.confirmButton.type) {
            return defaultButtonTextOptions.find(to => to.value === axnuiTheme.confirmButton.type);
        }
        return null;
    }, [
        defaultButtonTextOptions,
        axnuiTheme.confirmButton.type
    ]);

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.confirmButton.useCustomText}
                currentThemingPropertyValue={axnuiTheme.confirmButton.useCustomText}
                pageLevel={pageLevel}
            >
                <RadioInputFormGroup
                    controlId={`theming-action-button-use-custom-button-text-radio-input`}
                    automationId={`theming-action-button-use-custom-button-text-radio-input`}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.useCustomButtonText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleUseCustomButtonTextChange}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    value={axnuiTheme.confirmButton.useCustomText ? "true" : "false"}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.useCustomButtonText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetUseCustomButtonText}
                />
            </ThemingFormGroupWrapper>
            {axnuiTheme.confirmButton.useCustomText && (
                <>
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUIContent.confirmButton.buttonText}
                        currentThemingPropertyValue={axnuiContent.confirmButton.buttonText}
                        pageLevel={pageLevel}
                    >
                        <InputFormGroup
                            controlId={"theming-action-button-custom-text-input"}
                            automationId={"theming-action-button-custom-text-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.customButtonText.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.customButtonText.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleCustomButtonTextChange}
                            value={axnuiContent.confirmButton.buttonText || ""}
                            disabled={isDisabled}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.customButtonText.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetCustomButtonText}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.customButtonTextError}
                </>
            )}
            {!axnuiTheme.confirmButton.useCustomText && (
                <ThemingFormGroupWrapper
                    inheritedThemingPropertyValue={inheritedAXNUITheme.confirmButton.type}
                    currentThemingPropertyValue={axnuiTheme.confirmButton.type}
                    pageLevel={pageLevel}
                >
                    <SelectFormGroup
                        controlId={"theming-action-button-default-text-select-input"}
                        automationId={"theming-action-button-default-text-select-input"}
                        label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.defaultButtonText.label}
                        ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.defaultButtonText.label}
                        disabled={isDisabled}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={false}
                        layout={layout}
                        labelWidth={labelWidth}
                        onChange={handleDefaultButtonTextChange}
                        options={defaultButtonTextOptions}
                        selectedOption={selectedDefaultButtonTextOption}
                        afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.actionButton.inputs.defaultButtonText.tooltip}
                        afterLabelIcon={isDisabled ? null : faUndo}
                        afterLabelIconOnClick={handleResetDefaultButtonText}
                    />
                </ThemingFormGroupWrapper>
            )}
        </div>
    );
};